import type { QueryClient } from '@tanstack/react-query'
import { permissionsQueryKeys } from '../api/queryKeys'
import { refreshOwnCapabilities } from './ownCapabilities'

/**
 * Marks these permission groups' own pages stale once a membership change
 * from a list (the Permission Groups or Users grid) has saved. The list keeps
 * its own optimistic copy; each group's page reads a separate entry, which
 * refetches the next time it opens. The list itself isn't refetched, so an
 * edit still waiting to save never flicks back.
 */
export function refreshPermissionGroupPages(queryClient: QueryClient, groupIds: Iterable<string>) {
  for (const groupId of new Set(groupIds)) {
    void queryClient.invalidateQueries({
      queryKey: permissionsQueryKeys.permissionsUserGroup(groupId),
    })
  }
}

/**
 * Re-asks every saved assignment after any permission save. A group's tabs
 * read them by group and a table's or column's panel by resource, so a save
 * on one side would leave the other showing the old grants (and saving again
 * would create them twice). The grant may be your own, so what you may do
 * is re-asked too. Only open screens refetch now.
 */
export function refreshPermissionAssignments(queryClient: QueryClient) {
  refreshOwnCapabilities(queryClient)
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: permissionsQueryKeys.permissionsAssignedAll }),
    queryClient.invalidateQueries({ queryKey: permissionsQueryKeys.resourcePermissionEditors }),
  ])
}

/**
 * Re-asks the table list a group's Tables and Columns tabs show, once a table
 * is created or renamed. They read the permission catalog's own copy, not the
 * Tables list, so the table would be missing there or keep its old name. Each
 * table's fields are left alone.
 */
export function refreshPermissionTables(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    queryKey: permissionsQueryKeys.permissionsTables,
    exact: true,
  })
}

/**
 * Re-asks one table's fields on a group's Tables and Columns tabs, once a
 * field is created, renamed or deleted on it. They read the permission
 * catalog's own copy, not the table's schema, so the field would be missing
 * there, keep its old name, or linger after its delete (and granting on it
 * would fail).
 */
export function refreshPermissionTableFields(queryClient: QueryClient, tableId: string) {
  return queryClient.invalidateQueries({
    queryKey: permissionsQueryKeys.permissionsTableSchema(tableId),
  })
}
